import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { Close, Delete, AttachFile, InsertDriveFile, Delete as DeleteIcon } from "@mui/icons-material";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  criarAtividade,
  atualizarAtividade,
  excluirAtividade,
  moverAtividade,
  ICriarAtividadeRequest,
  IAtualizarAtividadeRequest,
  IAtividade,
  uploadDocumento,
  listarDocumentos,
  IDocumento,
  excluirDocumento,
} from "services/modules/tarefaDetalhe";
import { useNavigation } from "contexts/notificationsContext";
import configs from "Config";
import { isEqual } from "lodash";
import { normalizeFormData } from "../utils/dirtyState";

interface ModalAtividadeProps {
  isOpen: boolean;
  onClose: () => void;
  idTarefa: number;
  atividade?: IAtividade | null; // Se null, é criação
  onAtividadeSalva: () => void;
  idStatusInicial?: number | null; // Status inicial quando criar nova atividade
}

export const ModalAtividade = ({
  isOpen,
  onClose,
  idTarefa,
  atividade,
  onAtividadeSalva,
  idStatusInicial,
}: ModalAtividadeProps) => {
  const { openNotification } = useNavigation();
  // Item 6.1: após criar a atividade, mantemos o modal aberto e migramos para
  // modo edição guardando o id retornado, para um novo "Salvar" atualizar
  // (em vez de criar outra atividade duplicada).
  const [idAtividadeCriada, setIdAtividadeCriada] = useState<number | null>(null);
  // Status persistido da atividade recém-criada (base para detectar mudança de status no próximo Salvar)
  const [statusAtividadeCriada, setStatusAtividadeCriada] = useState<number | null>(null);
  const idAtividadeEfetivo = atividade?.idTarefaAtividade ?? idAtividadeCriada;
  const isEdicao = !!idAtividadeEfetivo;

  const [formData, setFormData] = useState<Partial<ICriarAtividadeRequest>>({
    idTarefa: idTarefa,
    titulo: "",
    descricao: "",
    idTarefaStatus: 1, // Default: "Novos" (status 1)
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dirty-state (N2.3): snapshot do formulário ao abrir/criar — base para
  // detectar alterações reais (descrição via stripHtml na normalização)
  const initialFormDataRef = useRef<Partial<ICriarAtividadeRequest>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Estados para documentos
  const [documentos, setDocumentos] = useState<IDocumento[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  
  // Documentos temporários (para nova atividade que ainda não foi salva)
  const [documentosTemporarios, setDocumentosTemporarios] = useState<File[]>([]);
  
  // Configuração do módulo Quill
  const quillModules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link", "image"],
        ["clean"],
      ],
    }),
    []
  );

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
    "image",
  ];

  // Colunas padrão do Kanban (mapeamento idTarefaStatus -> nome)
  const colunasKanban = [
    { id: 1, nome: "Novos" },
    { id: 2, nome: "Andamento" },
    { id: 3, nome: "Aguardando" },
    { id: 4, nome: "Concluídos" },
  ];

  // Carregar documentos da atividade ou sub-tarefa
  const carregarDocumentos = useCallback(async () => {
    if (!isOpen || !idTarefa) return;
    try {
      // Se estamos editando uma atividade existente, buscar documentos da atividade
      // Caso contrário, buscar documentos da sub-tarefa
      const idTarefaAtividade = atividade?.idTarefaAtividade || null;
      console.log("[ModalAtividade] Carregando documentos - idTarefa:", idTarefa, "idTarefaAtividade:", idTarefaAtividade, "atividade completa:", atividade);
      
      if (!idTarefaAtividade && isEdicao) {
        console.warn("[ModalAtividade] AVISO: idTarefaAtividade é null/undefined durante edição. Não será possível buscar documentos da atividade.");
      }
      
      const docs = await listarDocumentos({
        idTarefa: idTarefa,
        idTarefaAtividade: idTarefaAtividade,
      });
      console.log("[ModalAtividade] Documentos carregados:", docs, "Quantidade:", docs.length);
      
      if (docs.length === 0 && idTarefaAtividade && isEdicao) {
        console.warn("[ModalAtividade] AVISO: Nenhum documento encontrado para idTarefaAtividade:", idTarefaAtividade);
        console.warn("[ModalAtividade] Possíveis causas:");
        console.warn("  1. Documentos foram salvos antes da migration ser executada");
        console.warn("  2. Documentos não foram vinculados corretamente à atividade");
        console.warn("  3. Execute o script: corrigir_documentos_sem_idTarefaAtividade.sql");
      }
      
      setDocumentos(docs);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    }
  }, [isOpen, idTarefa, atividade, isEdicao]);

  // Inicializar formulário
  useEffect(() => {
    if (isOpen) {
      const inicial: Partial<ICriarAtividadeRequest> = atividade
        ? {
            idTarefa: atividade.idTarefa,
            titulo: atividade.titulo,
            descricao: atividade.descricao || "",
            idTarefaStatus: atividade.idTarefaStatus, // Usar idTarefaStatus em vez de idColunaKanban
          }
        : {
            idTarefa: idTarefa,
            titulo: "",
            descricao: "",
            idTarefaStatus: idStatusInicial || 1, // Usar status inicial se fornecido, senão "Novos"
          };
      setFormData(inicial);
      // Snapshot do formulário (N2.3): base para detectar alterações reais
      initialFormDataRef.current = inicial;
      setHasChanges(false);
      setErrors({});
      setDocumentosTemporarios([]); // Limpar documentos temporários ao abrir modal
      setIdAtividadeCriada(null); // Item 6.1: resetar id criado ao (re)abrir o modal
      setStatusAtividadeCriada(null);
    }
  }, [isOpen, atividade, idTarefa, idStatusInicial]);

  // Dirty-state por snapshot (N2.3): compara o formulário atual (normalizado)
  // com o snapshot capturado ao abrir/criar — evita falso positivo ao abrir
  // a atividade sem editar nada
  useEffect(() => {
    setHasChanges(
      !isEqual(
        normalizeFormData(formData),
        normalizeFormData(initialFormDataRef.current)
      )
    );
  }, [formData]);

  // Carregar documentos quando modal abrir ou atividade mudar
  useEffect(() => {
    if (isOpen && idTarefa) {
      // Se estamos editando, garantir que atividade esteja disponível antes de carregar documentos
      if (isEdicao && atividade) {
        // Pequeno delay para garantir que atividade esteja completamente carregada
        const timer = setTimeout(() => {
          console.log("[ModalAtividade] useEffect - Carregando documentos para edição, atividade:", atividade);
          carregarDocumentos();
        }, 300);
        return () => clearTimeout(timer);
      } else {
        // Para criação, carregar imediatamente
        carregarDocumentos();
      }
    } else {
      // Limpar documentos quando modal fechar
      setDocumentos([]);
    }
  }, [isOpen, idTarefa, atividade?.idTarefaAtividade, isEdicao, atividade, carregarDocumentos]);

  // Alterar campo
  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.titulo?.trim()) {
      newErrors.titulo = "O título é obrigatório";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar atividade
  const handleSalvar = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isEdicao && idAtividadeEfetivo) {
        // Status base: o original (prop) quando editando uma atividade existente,
        // ou o status persistido da atividade recém-criada (Item 6.1).
        const statusBase = atividade?.idTarefaStatus ?? statusAtividadeCriada ?? formData.idTarefaStatus ?? 1;
        // Obter o novo status do formulário
        const novoStatus = formData.idTarefaStatus || statusBase;
        const statusMudou = novoStatus !== statusBase;

        console.log("[ModalAtividade] Atualizando atividade:", {
          idTarefaAtividade: idAtividadeEfetivo,
          statusAtual: statusBase,
          novoStatus: novoStatus,
          statusMudou: statusMudou,
        });

        // Se o status mudou, usar moverAtividade primeiro
        if (statusMudou) {
          try {
            await moverAtividade({
              idTarefaAtividade: idAtividadeEfetivo,
              idTarefaStatusNovo: novoStatus,
            });
            console.log("[ModalAtividade] Atividade movida para status:", novoStatus);
          } catch (error) {
            console.error("[ModalAtividade] Erro ao mover atividade:", error);
            openNotification({
              isOpen: true,
              message: "Erro ao mover atividade. Verifique os dados e tente novamente.",
              type: "error",
            });
            setLoading(false);
            return;
          }
        }

        // Atualizar outros campos da atividade
        const request: IAtualizarAtividadeRequest = {
          idTarefaAtividade: idAtividadeEfetivo,
          titulo: formData.titulo || "",
          descricao: formData.descricao || null,
          idTarefaStatus: novoStatus, // Usar o novo status
        };

        console.log("[ModalAtividade] Atualizando atividade com request:", {
          idTarefaAtividade: request.idTarefaAtividade,
          titulo: request.titulo,
          idTarefaStatus: request.idTarefaStatus,
          descricaoLength: request.descricao?.length || 0,
        });

        const result = await atualizarAtividade(request);
        if (result) {
          // Manter sincronizada a base de status para o próximo Salvar (caso
          // tenha sido uma atividade recém-criada que ainda não tem prop).
          if (!atividade) {
            setStatusAtividadeCriada(novoStatus);
          }
          openNotification({
            isOpen: true,
            message: statusMudou
              ? "Atividade atualizada e movida com sucesso!"
              : "Atividade atualizada com sucesso!",
            type: "success",
          });
          // Item 6.1: NÃO fechar o modal ao Salvar — manter aberto.
          // O fechamento ocorre apenas pelo X/Cancelar.
          onAtividadeSalva();
        }
      } else {
        // Criar
        const request: ICriarAtividadeRequest = {
          idTarefa: idTarefa,
          titulo: formData.titulo || "",
          descricao: formData.descricao || null,
          idTarefaStatus: idStatusInicial || formData.idTarefaStatus || 1, // Usar status inicial se fornecido, senão do formulário
          replicarParaOutrasSubtarefas: true, // Sempre replicar quando criar (conforme regra do mockup)
        };

        console.log("[ModalAtividade] Criando atividade com request:", {
          idTarefa: request.idTarefa,
          titulo: request.titulo,
          replicarParaOutrasSubtarefas: request.replicarParaOutrasSubtarefas,
        });

        const result = await criarAtividade(request);
        if (result && result.idTarefaAtividade) {
          // Item 6.1: migrar para modo edição guardando o id retornado, para
          // que um novo "Salvar" atualize a atividade (em vez de duplicar).
          setIdAtividadeCriada(result.idTarefaAtividade);
          setStatusAtividadeCriada(request.idTarefaStatus ?? null);
          // Fazer upload dos documentos temporários com o idTarefaAtividade correto
          if (documentosTemporarios.length > 0) {
            try {
              setIsUploading(true);
              console.log("[ModalAtividade] Fazendo upload de", documentosTemporarios.length, "documento(s) para atividade", result.idTarefaAtividade);
              
              for (const file of documentosTemporarios) {
                console.log("[ModalAtividade] Upload documento:", file.name, "para idTarefaAtividade:", result.idTarefaAtividade);
                const uploadResult = await uploadDocumento(undefined, idTarefa, file, result.idTarefaAtividade);
                console.log("[ModalAtividade] Documento enviado com sucesso:", uploadResult);
              }
              
              const quantidadeDocumentos = documentosTemporarios.length;
              setDocumentosTemporarios([]);
              
              // Aguardar um pouco para garantir que o banco processou o commit
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Recarregar documentos para exibir os que foram anexados
              // Passar o idTarefaAtividade diretamente, já que atividade é null durante criação
              console.log("[ModalAtividade] Recarregando documentos após upload...");
              try {
                const docs = await listarDocumentos({
                  idTarefa: idTarefa,
                  idTarefaAtividade: result.idTarefaAtividade,
                });
                console.log("[ModalAtividade] Documentos carregados após upload:", docs);
                setDocumentos(docs);
              } catch (error) {
                console.error("Erro ao carregar documentos após upload:", error);
              }
              
              openNotification({
                isOpen: true,
                message: `Atividade criada e ${quantidadeDocumentos} documento(s) anexado(s) com sucesso!`,
                type: "success",
              });
            } catch (error) {
              console.error("Erro ao fazer upload dos documentos temporários:", error);
              openNotification({
                isOpen: true,
                message: "Atividade criada, mas houve erro ao anexar alguns documentos.",
                type: "warning",
              });
            } finally {
              setIsUploading(false);
            }
          } else {
            openNotification({
              isOpen: true,
              message: "Atividade criada com sucesso!",
              type: "success",
            });
          }
          
          // Item 6.1: NÃO fechar o modal após criar — manter aberto (já migrado
          // para modo edição acima). O fechamento ocorre só pelo X/Cancelar.
          onAtividadeSalva();
        }
      }
    } catch (error: any) {
      console.error("Erro ao salvar atividade:", error);
      // Exibir a mensagem real do CustomResponse: o service passa pelo
      // getErrorsByApi, que lança Error com as mensagens do backend em
      // error.message (item 5 do relatório FASE 04).
      openNotification({
        isOpen: true,
        message: error?.message || "Não foi possível salvar a atividade.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Excluir atividade
  const handleExcluir = async () => {
    if (!atividade) return;

    if (!window.confirm("Tem certeza que deseja excluir esta atividade?")) {
      return;
    }

    setLoading(true);
    try {
      const result = await excluirAtividade(atividade.idTarefaAtividade);
      if (result) {
        openNotification({
          isOpen: true,
          message: "Atividade excluída com sucesso!",
          type: "success",
        });
        onAtividadeSalva();
        onClose();
      }
    } catch (error) {
      console.error("Erro ao excluir atividade:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível excluir a atividade.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handlers para documentos
  const handleUploadDocumento = async (file: File) => {
    setIsUploading(true);
    try {
      // Se estamos editando uma atividade existente, fazer upload imediatamente
      if (isEdicao && atividade?.idTarefaAtividade) {
        const idTarefaAtividade = atividade.idTarefaAtividade;
        await uploadDocumento(undefined, idTarefa, file, idTarefaAtividade); // idTarefa aqui é na verdade idSubtarefa
        openNotification({
          isOpen: true,
          message: "Documento anexado com sucesso!",
          type: "success",
        });
        await carregarDocumentos();
      } else {
        // Se estamos criando uma nova atividade, armazenar temporariamente
        // O upload será feito após criar a atividade
        setDocumentosTemporarios((prev) => [...prev, file]);
        openNotification({
          isOpen: true,
          message: "Documento será anexado após salvar a atividade.",
          type: "info",
        });
      }
    } catch (error) {
      console.error("Erro ao anexar documento:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível anexar o documento.",
        type: "error",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExcluirDocumento = async (idDocumento: number) => {
    if (!window.confirm("Deseja excluir este documento?")) return;

    try {
      await excluirDocumento(idDocumento);
      openNotification({
        isOpen: true,
        message: "Documento excluído com sucesso!",
        type: "success",
      });
      await carregarDocumentos();
    } catch (error) {
      console.error("Erro ao excluir documento:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível excluir o documento.",
        type: "error",
      });
    }
  };

  // Remover documento temporário
  const handleRemoverDocumentoTemporario = (index: number) => {
    setDocumentosTemporarios((prev) => prev.filter((_, i) => i !== index));
  };

  // Função para inserir link de documento no editor
  const inserirLinkDocumento = useCallback((documento: IDocumento) => {
    if (!quillRef.current) {
      console.warn("[inserirLinkDocumento] quillRef não está disponível");
      return;
    }

    try {
      const quill = quillRef.current.getEditor();
      if (!quill) {
        console.warn("[inserirLinkDocumento] Editor Quill não está disponível");
        return;
      }

      const range = quill.getSelection(true);
      // Usar a URL base da API configurada (mesma base usada pelo serviço)
      const apiBaseUrl = configs.REACT_APP_BASE_URL || window.location.origin;
      // Remover /api do final se existir, pois vamos adicionar o caminho completo
      const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
      const link = `${baseUrl}/api/TarefaDetalhe/DownloadDocumento/${documento.idTarefaDocumento}`;
      
      if (range) {
        quill.insertText(range.index, documento.nomeOriginal, 'link', link);
        // setSelection espera dois parâmetros: index e length (ou um objeto RangeStatic)
        const newIndex = range.index + documento.nomeOriginal.length;
        quill.setSelection(newIndex, 0);
      } else {
        // Se não há seleção, inserir no final
        const length = quill.getLength();
        quill.insertText(length - 1, documento.nomeOriginal, 'link', link);
        // setSelection espera dois parâmetros: index e length (ou um objeto RangeStatic)
        const newIndex = length - 1 + documento.nomeOriginal.length;
        quill.setSelection(newIndex, 0);
      }
    } catch (error) {
      console.error("[inserirLinkDocumento] Erro ao inserir link:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível inserir o link no editor.",
        type: "error",
      });
    }
  }, [openNotification]);

  // Fechamento com guard (N2.3): confirma descarte quando há alterações reais
  const handleClose = useCallback(() => {
    if (
      hasChanges &&
      !window.confirm(
        "Há alterações não salvas. Deseja realmente fechar sem salvar?"
      )
    ) {
      return;
    }
    onClose();
  }, [hasChanges, onClose]);

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {isEdicao ? "Editar Atividade" : "Nova Atividade"}
        <IconButton
          aria-label="fechar"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Título */}
        <TextField
          autoFocus
          margin="dense"
          label="Título *"
          type="text"
          fullWidth
          variant="outlined"
          size="small"
          value={formData.titulo}
          onChange={(e) => handleChange("titulo", e.target.value)}
          error={!!errors.titulo}
          helperText={errors.titulo}
          sx={{ mb: 2 }}
        />

        {/* Detalhes da Atividade - Editor de Texto Rico */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Detalhes da Atividade
          </Typography>
          <ReactQuill
            theme="snow"
            value={formData.descricao || ""}
            onChange={(value) => handleChange("descricao", value)}
            modules={quillModules}
            formats={quillFormats}
            style={{
              height: "200px",
              marginBottom: "40px",
            }}
            ref={quillRef}
          />
        </Box>

        {/* Coluna do Kanban - Oculto na criação, será definido pelo status inicial */}
        {isEdicao && (
          <FormControl fullWidth margin="dense" size="small" sx={{ mb: 2 }}>
            <InputLabel>Coluna do Kanban</InputLabel>
            <Select
              value={formData.idTarefaStatus || atividade?.idTarefaStatus || 1}
              label="Coluna do Kanban"
              onChange={(e) => handleChange("idTarefaStatus", Number(e.target.value))}
            >
              {colunasKanban.map((coluna) => (
                <MenuItem key={coluna.id} value={coluna.id}>
                  {coluna.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Seção de Anexar Documentos */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Documentos Anexados
          </Typography>
          
          {/* Botão para anexar documento */}
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleUploadDocumento(file);
              }
            }}
            disabled={isUploading}
          />
          <Button
            variant="outlined"
            startIcon={<AttachFile />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            size="small"
            sx={{ mb: 2 }}
          >
            {isUploading ? "Anexando..." : "Anexar Documento"}
          </Button>

          {/* Lista de documentos temporários (apenas quando criando nova atividade) */}
          {!isEdicao && documentosTemporarios.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Documentos serão anexados após salvar a atividade:
              </Typography>
              <List dense sx={{ bgcolor: "#fff3cd", borderRadius: 1, p: 1, border: "1px solid #ffc107" }}>
                {documentosTemporarios.map((file, index) => (
                  <ListItem
                    key={`temp-${index}`}
                    sx={{
                      py: 0.5,
                      "&:hover": { bgcolor: "#ffe082" },
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoverDocumentoTemporario(index)}
                        disabled={loading || isUploading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <InsertDriveFile fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {(file.size / 1024).toFixed(2)} KB
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Lista de documentos salvos */}
          {documentos.length > 0 && (
            <List dense sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 1 }}>
              {documentos.map((doc) => (
                <ListItem
                  key={doc.idTarefaDocumento}
                  sx={{
                    py: 0.5,
                    "&:hover": { bgcolor: "#e0e0e0" },
                    cursor: "pointer",
                  }}
                  onClick={() => inserirLinkDocumento(doc)}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcluirDocumento(doc.idTarefaDocumento);
                      }}
                      disabled={loading || isUploading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <InsertDriveFile fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => inserirLinkDocumento(doc)}
                      >
                        {doc.nomeOriginal}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(doc.dataUpload).toLocaleDateString("pt-BR")}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Informação sobre anexo de documentos */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "#e8f5e9", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <AttachFile sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 14 }} />
              Clique no nome do documento para inserir um link no editor de texto. Os documentos também podem ser acessados na aba "Documentos" da respectiva sub-tarefa.
            </Typography>
          </Box>
        </Box>

        {/* Informações adicionais (apenas em edição) */}
        {isEdicao && atividade && (
          <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="caption" color="textSecondary">
              Criado por: {atividade.nomeUsuarioCriacao || "Desconhecido"}
            </Typography>
            <br />
            <Typography variant="caption" color="textSecondary">
              Data de criação:{" "}
              {atividade.dataCriacao
                ? new Date(atividade.dataCriacao).toLocaleString("pt-BR")
                : "-"}
            </Typography>
            {atividade.dataAtualizacao && (
              <>
                <br />
                <Typography variant="caption" color="textSecondary">
                  Última atualização:{" "}
                  {new Date(atividade.dataAtualizacao).toLocaleString("pt-BR")}
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          justifyContent: isEdicao ? "space-between" : "flex-end",
          gap: 1,
        }}
      >
        {isEdicao && (
          <Button
            onClick={handleExcluir}
            variant="outlined"
            color="error"
            disabled={loading}
            startIcon={<Delete />}
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Excluir
          </Button>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            disabled={loading}
            sx={{
              minWidth: 120,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            CANCELAR
          </Button>
          <Button
            onClick={handleSalvar}
            variant="contained"
            color="primary"
            disabled={loading || (isEdicao && !hasChanges)}
            sx={{
              minWidth: 120,
              backgroundColor: "#3C90AD",
              textTransform: "uppercase",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#2d7a94",
              },
              "&:disabled": {
                backgroundColor: "#9e9e9e",
                color: "#fff",
              },
            }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {loading ? "SALVANDO..." : "SALVAR"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

